import { writable } from 'svelte/store';
import { api } from '../framework/js/api.js';
import { ACTIVE_BRIEF_TYPE_UUID } from '../framework/js/briefConstants.js';

/**
 * @typedef {Object} BriefQuestion
 * @property {string} uuid
 * @property {string} question_content
 * @property {string} question_prompt
 * @property {string} question_type
 * @property {unknown} question_metadata
 * @property {unknown} question_prefix
 */

/**
 * @typedef {Object} BriefStep
 * @property {number} id
 * @property {string} step_type
 * @property {string} step_next
 * @property {string} step_prev
 * @property {boolean} is_final
 * @property {string} question_uuid
 * @property {string} xplorit_genai_url
 * @property {string} step_content
 * @property {boolean} login_required
 * @property {number} cost
 * @property {string} [img]
 * @property {boolean} [bubble]
 * @property {string} [bubble_content]
 * @property {number} [bubble_show_1]
 * @property {string} [bubble_location]
 * @property {string} [bubble_size]
 * @property {string} [bubble_icon]
 * @property {string} [bubble_class]
 * @property {string} [bubble_content_2]
 * @property {number} [bubble_show_2]
 * @property {string} [question_content]
 * @property {string} [question_prompt]
 * @property {string} [question_type]
 * @property {unknown} [question_metadata]
 * @property {unknown} [question_prefix]
 * @property {Record<string, unknown>} [xplorit_genai_output]
 */

/** @type {BriefStep[]} */
export let initialSteps = [
    {
        id: 1,
        step_type: "question",
        step_next: "Next",
        step_prev: "Back",
        is_final: true,
        question_uuid: "6fcc554a-0ffe-4e44-8c06-48e343c29988",
        xplorit_genai_url: "",
        step_content: "",
        login_required : false,
        cost: 0,
        img: "https://via.placeholder.com/1024",
        bubble: true,
        bubble_content: "Great start! But I would need to know more.",
        bubble_show_1: 300,
        bubble_location: "bottom",
        bubble_size: "normal",
        bubble_icon: "goodTwo",
        bubble_class: "centered-content",
        bubble_content_2: "You’ve got me thinking now",
        bubble_show_2: 1000,
    },
    {
        id: 2,
        step_type: "question",
        step_next: "Next",
        step_prev: "Back",
        is_final: true,
        question_uuid: "6fcc554a-0ffe-4e44-8c06-48e343c29877",
        xplorit_genai_url: "",
        step_content: "",
        login_required : false,
        cost: 0,
        img: "https://via.placeholder.com/1024",
        bubble: true,
        bubble_content: "I have a better understanding of what you need to know from this research. Let’s try to align the research with the business objectives",
        bubble_show_1: 300,
        bubble_location: "bottom-skew",
        bubble_size: "large",
        bubble_icon: "thinkingFace",
        bubble_class: "centered-content",
        bubble_content_2: "Nice job! Now, let’s try to align the research with the business objectives",
        bubble_show_2: 1000,

    },
    {
        id: 3,
        step_type: "question",
        step_next: "Next",
        step_prev: "Back",
        is_final: false,
        question_uuid: "17d633fa-25ff-4e56-91d1-aabcfcfb719c",
        xplorit_genai_url: "",
        step_content: "",
        login_required : false,
        cost: 0,
        img: "https://via.placeholder.com/1024",
        bubble: true,
        bubble_content: "I can see how the insights from this research will be useful. Let’s dive in deeper to make sure they are relevant and actionable.",
        bubble_show_1: 300,
        bubble_location: "bottom-skew",
        bubble_size: "large",
        bubble_icon: "lightBulb",
        bubble_class: "centered-content",
        bubble_content_2: "Great stuff! Next, can you tell me the profile of the research participants?",
        bubble_show_2: 1000,

    },
    {
        id: 4,
        step_type: "question",
        step_next: "Next",
        step_prev: "Back",
        is_final: true,
        question_uuid: "a81c1255-14bf-4af6-903d-3120b867e4c1",
        xplorit_genai_url: "",
        step_content: "",
        login_required : false,
        cost: 0,
        img: "https://via.placeholder.com/1024",
        bubble: false,
        bubble_content: ""
    },
    {
        id: 5,
        step_type: "question",
        step_next: "Next",
        step_prev: "Back",
        is_final: true,
        question_uuid: "0858789e-992b-472f-8f6c-5e1aaf9ccc91",
        xplorit_genai_url: "",
        step_content: "",
        login_required : false,
        cost: 0,
        img: "https://via.placeholder.com/1024",
        bubble: true,
        bubble_content: "What else would you like to know that will be useful?",
        bubble_show_1: 300,
        bubble_location: "bottom",
        bubble_size: "small",
        bubble_icon: "questionMark",
        bubble_class: "centered-y-content",
        bubble_content_2: "Now let’s see what’s the best way to gather this data and information",
        bubble_show_2: 1000,
    },
    {
        id: 6,
        step_type: "question",
        step_next: "Submit",
        step_prev: "Back",
        is_final: true,
        question_uuid: "8a7c554b-1ffe-4e44-8c06-48e343c29123",
        xplorit_genai_url: "",
        step_content: "",
        login_required : false,
        cost: 0,
        img: "https://via.placeholder.com/1024",
        bubble: true,
        bubble_content: "You’ve got me thinking now",
        bubble_location: "right",
        bubble_size: "small",
        bubble_icon: "thinking",
        bubble_class: "centered-content"

    },
    {
        id: 7,
        step_type: "xplorit_genai",
        step_next: "Back",
        step_prev: "",
        is_final: true,
        question_uuid: "",
        xplorit_genai_url: "",
        xplorit_genai_output: {},
        step_content: "",
        login_required : true,
        cost: 0
    }


];

/**
 * @param {string|null|undefined} [typeBriefUuid=null]
 * @returns {Promise<BriefStep[]|undefined>}
 */
async function fetchQuestionsAndUpdateSteps(typeBriefUuid = null) {
    const effectiveTypeUuid = typeBriefUuid || ACTIVE_BRIEF_TYPE_UUID;
    const response = await api.getBriefQuestions(effectiveTypeUuid);
    
    if (!response.success) {
        console.warn('[BriefStepStore] fetchQuestionsAndUpdateSteps - API response not successful');
        return;
    }
    var respondeData = response.data;
    
    // Handle paginated response - LengthAwarePaginatorResource returns {success, items, paging, errors}
    if (respondeData && !Array.isArray(respondeData)) {
        // Check common pagination structures (items is the standard for this backend)
        if (respondeData.items && Array.isArray(respondeData.items)) {
            respondeData = respondeData.items;
        } else if (respondeData.list && Array.isArray(respondeData.list)) {
            respondeData = respondeData.list;
        } else if (respondeData.data && Array.isArray(respondeData.data)) {
            respondeData = respondeData.data;
        }
    }
    
    // Rebuild steps from API whenever we have questions (avoids stale hardcoded UUIDs → "undefined" labels)
    if (Array.isArray(respondeData) && respondeData.length > 0) {
        /** @type {BriefStep[]} */
        const mapped = respondeData.map((/** @type {BriefQuestion} */ question, index) => {
            return {
                id: index + 1,
                step_type: "question",
                step_next: "Next",
                step_prev: "Back",
                is_final: false,
                question_uuid: question.uuid,
                xplorit_genai_url: "",
                step_content: "",
                login_required: false,
                cost: 0,
                img: "https://via.placeholder.com/1024",
                bubble: false,
                question_content: question.question_content,
                question_prompt: question.question_prompt,
                question_type: question.question_type,
                question_metadata: question.question_metadata,
                question_prefix: question.question_prefix,
            };
        });

        /** @type {BriefStep[]} */
        const newSteps = [];
        mapped.forEach((step) => {
            newSteps.push(step);
            const meta = parseQuestionMetadata(step.question_metadata);
            if (meta.readback_after) {
                newSteps.push({
                    id: step.id + 0.5,
                    step_type: "readback",
                    step_next: "That's right",
                    step_prev: "Back",
                    is_final: false,
                    question_uuid: "",
                    xplorit_genai_url: "",
                    step_content: "",
                    login_required: false,
                    cost: 0,
                });
            }
        });

        newSteps.forEach((step, index) => {
            step.id = index + 1;
            const isLastQuestion = step.step_type === "question" &&
                !newSteps.slice(index + 1).some((s) => s.step_type === "question");
            if (isLastQuestion) {
                step.step_next = "Write the brief";
                step.is_final = true;
            }
        });

        /** @type {BriefStep} */
        const genaiStep = {
            id: newSteps.length + 1,
            step_type: "xplorit_genai",
            step_next: "Back",
            step_prev: "",
            is_final: true,
            question_uuid: "",
            xplorit_genai_url: "",
            xplorit_genai_output: {},
            step_content: "",
            login_required: true,
            cost: 0
        };
        newSteps.push(genaiStep);
        
        initialSteps = newSteps;
        BriefStepStore.set(initialSteps);
        return initialSteps;
    }
    
    // Default behavior: update existing steps with question content
    const updatedSteps = initialSteps.map(step => {
        const matchingQuestion = respondeData.find((/** @type {BriefQuestion} */ q) => q.uuid === step.question_uuid);
        if (matchingQuestion) {
            return {
                ...step,
                question_content: matchingQuestion.question_content,
                question_prompt: matchingQuestion.question_prompt,
                question_type: matchingQuestion.question_type,
                question_metadata: matchingQuestion.question_metadata,
            };
        }
        return step;
    });
    initialSteps = updatedSteps;
    BriefStepStore.set(initialSteps);
    return initialSteps;
}

/**
 * Load brief steps from the API. Pass a brief type UUID to use that type's questions (e.g. v2);
 * omit or pass null to use legacy hardcoded steps.
 * @param {string|null|undefined} [typeBriefUuid=null] - Brief type UUID, or null for legacy steps
 */
/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function parseQuestionMetadata(raw) {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        return /** @type {Record<string, unknown>} */ (raw);
    }
    if (typeof raw === 'string') {
        try {
            return parseQuestionMetadata(JSON.parse(raw));
        } catch {
            return {};
        }
    }
    return {};
}

/**
 * @param {string | null} [typeBriefUuid]
 */
export async function initializeStore(typeBriefUuid = null) {
    await fetchQuestionsAndUpdateSteps(typeBriefUuid);
}



export const BriefStepStore = writable(initialSteps || []);
